import { useState } from 'react';
import type { StepProps } from '../OBWWizard';

// Phase 5c.5 (ADR-030 D2 Step 6) — multi-field form with jurisdiction-aware
// copy (ABN vs NZBN vs EIN hint label).

/**
 * The business-registration hint, keyed by the country given at the `residency` step
 * (ADR-124 I3). The country is no longer asked HERE: the residency step is the one place a
 * business declares it, and a second select later in the journey would overwrite that answer in
 * the flattened step data the completion step provisions from — putting the account in a zone
 * the customer never chose. Any other country gets the generic label.
 */
const REG_NUMBER_LABEL: Record<string, string> = {
  AU: 'ABN (optional)',
  NZ: 'NZBN (optional)',
  US: 'EIN (optional)',
};

/** Permissive shape check — see the staff fork's copy for the reasoning. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StepProfile({ data, onNext, isRequired, actor, accountId, presentation }: StepProps) {
  // ADR-073 Am.5 §A5.6 — condensed for the public modal's frame (no scrolling): no section headings,
  // and the website — already authorised at `domain-verify` — is carried, not asked again.
  const compact = presentation === 'embedded';
  const [businessName, setBusinessName] = useState<string>((data?.businessName as string) ?? '');
  const [businessEmail, setBusinessEmail] = useState<string>((data?.businessEmail as string) ?? '');
  // Read-only here — answered at the `residency` step (ADR-124 I3).
  const jurisdiction = typeof data?.jurisdiction === 'string' ? (data.jurisdiction as string) : '';
  const [registrationNumber, setRegistrationNumber] = useState<string>((data?.registrationNumber as string) ?? '');
  const [websiteUrl, setWebsiteUrl] = useState<string>((data?.domainUrl as string) ?? '');
  // ADR-073 Amendment 2 (A2.4) — "Nexa Consultant" records which staff member
  // onboarded the participant. Admin-run journeys only (BOW, manual-onboard);
  // auto-filled from the logged-in staff identity, prefilled via `nexaConsultant`
  // in the seeded step data (BOW launch token carries the staff name).
  const isAdminRun = actor === 'nexa-admin';
  const [nexaConsultant, setNexaConsultant] = useState<string>((data?.nexaConsultant as string) ?? '');
  const [error, setError] = useState('');

  /**
   * ⭐ MIRRORS THE STAFF FORK AND THE `profile` VALIDATOR (§9 copy-fork).
   *
   * On this surface the ONLY account-less mount is the /onboard/bow beta landing
   * (accountId ''), which provisions the User + Account from this form —
   * BowCompleteStep already refuses without an email. Every other journey here
   * carries a real account id and had its owner's email captured at the public
   * sign-up form, so the field stays the optional business CONTACT address it has
   * always been. The signed-off sign-up SOW is therefore unchanged.
   */
  const emailRequired = isRequired && !accountId;

  function commit(next: () => void) {
    setError('');
    if (isRequired && !businessName.trim()) { setError('Please enter your business name.'); return; }
    if (emailRequired && !businessEmail.trim()) {
      setError('Please enter the business email — the activation link is sent there.');
      return;
    }
    if (emailRequired && !EMAIL_SHAPE.test(businessEmail.trim())) {
      setError('Please enter a valid business email address, e.g. name@business.com.');
      return;
    }
    onNext({
      businessName: businessName.trim(),
      businessEmail: businessEmail.trim(),
      registrationNumber: registrationNumber.trim() || undefined,
      websiteUrl: websiteUrl.trim(),
      ...(isAdminRun ? { nexaConsultant: nexaConsultant.trim() || undefined } : {}),
    });
    next();
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Business profile</h2>
      <p className="obw-subtitle">
        {compact
          ? 'This personalises your dashboard and reports.'
          : 'Tell us about your business. This information personalises trust signals across your dashboard and per-customer reports.'}
      </p>

      {!compact && <p className="obw-form-section">Identity</p>}
      <div className="obw-form-grid-2">
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-business-name">
            Business name {isRequired && <span aria-hidden="true">*</span>}
          </label>
          <input
            id="obw-business-name"
            className="obw-form-input"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Acme Pty Ltd"
            required={isRequired}
          />
        </div>
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-business-email">
            Business email {emailRequired && <span aria-hidden="true">*</span>}
          </label>
          <input
            id="obw-business-email"
            className="obw-form-input"
            type="email"
            value={businessEmail}
            onChange={(e) => setBusinessEmail(e.target.value)}
            placeholder="hello@yourbusiness.com"
            autoComplete="email"
            required={emailRequired}
          />
          {emailRequired && (
            <p className="obw-form-hint">The activation link is sent to this address.</p>
          )}
        </div>
      </div>

      {!compact && <p className="obw-form-section">Registration</p>}
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-reg-number">
          {REG_NUMBER_LABEL[jurisdiction] ?? 'Business registration (optional)'}
        </label>
        <input
          id="obw-reg-number"
          className="obw-form-input"
          type="text"
          value={registrationNumber}
          onChange={(e) => setRegistrationNumber(e.target.value)}
          placeholder={jurisdiction === 'AU' ? '11 222 333 444' : jurisdiction === 'NZ' ? '9429 0000 0000 0' : jurisdiction === 'US' ? 'XX-XXXXXXX' : ''}
        />
      </div>

      {!compact && <p className="obw-form-section">Web presence</p>}
      {!compact && <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-website-url">Primary website URL</label>
        <input
          id="obw-website-url"
          className="obw-form-input"
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://yourbusiness.com"
        />
        <p className="obw-form-hint">Pre-filled from the domain you authorised. Update if your primary website differs.</p>
      </div>}

      {isAdminRun && (
        <>
          <p className="obw-form-section">Nexanomics</p>
          <div className="obw-form-group">
            <label className="obw-form-label" htmlFor="obw-nexa-consultant">Nexa Consultant</label>
            <input
              id="obw-nexa-consultant"
              className="obw-form-input"
              type="text"
              value={nexaConsultant}
              onChange={(e) => setNexaConsultant(e.target.value)}
              placeholder="Consultant name"
            />
            <p className="obw-form-hint">The Nexanomics consultant onboarding this participant. Pre-filled from your staff profile.</p>
          </div>
        </>
      )}

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <div className="portal-wizard-nav">
        {/* In the frame the engine's footer already offers "Skip — I'll do this later". */}
        {!isRequired && !compact && (
          <button type="button" className="btn-secondary" onClick={() => commit(() => undefined)}>
            Skip for now
          </button>
        )}
        <button type="button" className="obw-btn-primary" onClick={() => commit(() => undefined)}>
          Continue
        </button>
      </div>
    </div>
  );
}
